import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, FolderOpen, FileText, Image as ImageIcon, Film, FileSpreadsheet, Presentation, File as FileIcon, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, fmtRel } from "@/lib/api";

function mimeIcon(m) {
  if (!m) return FileIcon;
  if (m.includes("folder")) return FolderOpen;
  if (m.startsWith("image/")) return ImageIcon;
  if (m.startsWith("video/")) return Film;
  if (m.includes("spreadsheet")) return FileSpreadsheet;
  if (m.includes("presentation")) return Presentation;
  if (m.includes("document") || m.includes("text")) return FileText;
  return FileIcon;
}

export default function DrivePicker({ open, onOpenChange, onPick, title = "Elegir archivo en Google Drive", filterMime }) {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => { const t = setTimeout(() => setSearch(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["drive", search],
    queryFn: async () => (await api.get(`/google/drive${search ? `?q=${encodeURIComponent(search)}` : ""}`)).data,
    enabled: open,
  });

  const files = (data?.files || []).filter((f) => !filterMime || (f.mimeType && filterMime.some((m) => f.mimeType.startsWith(m))));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="drive-picker">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Mi unidad y unidades compartidas. Solo lectura.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en Drive…" className="pl-8" data-testid="drive-search-input" />
        </div>
        <div className="-mx-1 max-h-[420px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-10 text-sm text-muted-foreground" data-testid="drive-loading">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando archivos…
            </div>
          ) : error ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="drive-error">
              Conecta tu cuenta de Google en Integraciones para ver tu Drive.
            </div>
          ) : files.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="drive-empty">
              {search ? `No encontramos archivos con «${search}».` : "Tu Drive está vacío o aún no se cargó."}
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="drive-list">
              {files.map((f) => {
                const Icon = mimeIcon(f.mimeType);
                return (
                  <li key={f.id}>
                    <button
                      onClick={() => { onPick(f); onOpenChange(false); }}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent"
                      data-testid="drive-item"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px]">{f.name}</div>
                        <div className="font-mono text-[10.5px] text-muted-foreground">
                          {f.driveId ? "Unidad compartida · " : ""}{f.owners?.[0]?.displayName || "—"} · {fmtRel(f.modifiedTime)}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" tabIndex={-1}>Usar</Button>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
